import React from 'react';

const WhyChooseVallenAI = ({ 
  title = "Why Choose Vallen AI?",
  subtitle = "Discover what sets us apart in the competitive AI landscape",
  benefits = [
    {
      icon: "bi-graph-up-arrow",
      title: "Business Impact Delivery",
      description: "We deliver measurable business value with AI solutions that directly impact your bottom line and operational efficiency."
    },
    {
      icon: "bi-clock-history",
      title: "24/7 Availability & Support", 
      description: "Round-the-clock support with unlimited free revisions and deep industrial AI project experience across multiple sectors."
    },
    {
      icon: "bi-database",
      title: "Data Centric AI Approach",
      description: "Our data-first methodology ensures your AI solutions are built on solid foundations with clean, validated, and optimized datasets."
    },
    {
      icon: "bi-shield-check",
      title: "Money Back Guarantee",
      description: "100% risk-free investment with our comprehensive money-back guarantee if we don't meet your specified requirements."
    }
  ],
  variant = "default" // "default", "compact", "minimal"
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "compact":
        return {
          container: {
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(14, 165, 233, 0.2)',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden'
          },
          title: {
            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
            fontWeight: '800',
            color: '#1e293b',
            marginBottom: '0.75rem',
            textAlign: 'center'
          },
          subtitle: {
            fontSize: '1rem',
            color: '#64748b',
            maxWidth: '500px',
            margin: '0 auto 2rem',
            lineHeight: '1.5'
          },
          card: {
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid rgba(14, 165, 233, 0.1)',
            transition: 'all 0.3s ease',
            height: '100%',
            position: 'relative',
            overflow: 'hidden'
          },
          iconContainer: {
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)'
          },
          cardTitle: {
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '0.5rem'
          },
          cardDescription: {
            fontSize: '0.9rem',
            color: '#64748b',
            lineHeight: '1.5',
            margin: 0
          }
        };
      case "minimal":
        return {
          container: {
            background: 'transparent',
            border: 'none',
            borderRadius: '0',
            padding: '1rem 0',
            boxShadow: 'none',
            position: 'relative',
            overflow: 'hidden'
          },
          title: {
            fontSize: 'clamp(1.2rem, 2vw, 1.5rem)',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '0.5rem',
            textAlign: 'center'
          },
          subtitle: {
            fontSize: '0.9rem',
            color: '#64748b',
            maxWidth: '400px',
            margin: '0 auto 1.5rem',
            lineHeight: '1.4'
          },
          card: {
            background: 'transparent',
            borderRadius: '8px',
            padding: '1rem',
            border: 'none',
            transition: 'all 0.3s ease',
            height: '100%',
            position: 'relative',
            overflow: 'hidden'
          },
          iconContainer: {
            width: '32px',
            height: '32px',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)'
          },
          cardTitle: {
            fontSize: '1rem',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '0.25rem'
          },
          cardDescription: {
            fontSize: '0.85rem',
            color: '#64748b',
            lineHeight: '1.4',
            margin: 0
          }
        };
      default:
        return {
          container: {
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(14, 165, 233, 0.2)',
            borderRadius: '24px',
            padding: '3rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden'
          },
          title: {
            fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
            fontWeight: '800',
            color: '#1e293b',
            marginBottom: '1rem',
            textAlign: 'center'
          },
          subtitle: {
            fontSize: '1.1rem',
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto 2.5rem',
            lineHeight: '1.6'
          },
          card: {
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '20px',
            padding: '2rem',
            border: '1px solid rgba(14, 165, 233, 0.1)',
            transition: 'all 0.3s ease',
            height: '100%',
            position: 'relative',
            overflow: 'hidden'
          },
          iconContainer: {
            width: '50px',
            height: '50px',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)'
          },
          cardTitle: {
            fontSize: '1.2rem',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '0.5rem'
          },
          cardDescription: {
            fontSize: '1rem',
            color: '#64748b',
            lineHeight: '1.6',
            margin: 0
          }
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className="row justify-content-center mt-5">
      <div className="col-lg-12">
        <div style={styles.container}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 10% 20%, rgba(14, 165, 233, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 90% 80%, rgba(14, 165, 233, 0.03) 0%, transparent 50%)
            `,
            zIndex: 1
          }}></div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <div className="text-center mb-5">
              <h3 style={styles.title}>
                {title.includes('Vallen AI') ? (
                  <>
                    {title.split('Vallen AI')[0]}
                    <span style={{ color: '#0ea5e9' }}>Vallen AI</span>
                    {title.split('Vallen AI')[1]}
                  </>
                ) : (
                  title
                )}
              </h3>
              <p style={styles.subtitle}>
                {subtitle}
              </p>
            </div>

            <div className="row g-3 g-md-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="col-12 col-md-6">
                  <div 
                    style={{
                      ...styles.card,
                      padding: variant === 'minimal' ? 'clamp(1rem, 3vw, 1.5rem)' : 
                               variant === 'compact' ? 'clamp(1.25rem, 3.5vw, 1.75rem)' : 
                               'clamp(1.5rem, 4vw, 2rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(14, 165, 233, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.1)';
                    }}
                  >
                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-start gap-3 gap-sm-2">
                      <div style={{
                        ...styles.iconContainer,
                        width: variant === 'minimal' ? 'clamp(28px, 6vw, 32px)' : 
                               variant === 'compact' ? 'clamp(32px, 7vw, 40px)' : 
                               'clamp(40px, 8vw, 50px)',
                        height: variant === 'minimal' ? 'clamp(28px, 6vw, 32px)' : 
                                variant === 'compact' ? 'clamp(32px, 7vw, 40px)' : 
                                'clamp(40px, 8vw, 50px)',
                        margin: '0 auto',
                        flexShrink: 0
                      }}>
                        <i className={`bi ${benefit.icon}`} style={{
                          color: 'white',
                          fontSize: variant === 'minimal' ? 'clamp(0.7rem, 2vw, 0.9rem)' : 
                                   variant === 'compact' ? 'clamp(0.8rem, 2.5vw, 1rem)' : 
                                   'clamp(1rem, 3vw, 1.2rem)'
                        }}></i>
                      </div>
                      <div className="flex-grow-1 text-center text-sm-start">
                        <h4 style={{
                          ...styles.cardTitle,
                          fontSize: variant === 'minimal' ? 'clamp(0.9rem, 2.5vw, 1rem)' : 
                                   variant === 'compact' ? 'clamp(1rem, 2.8vw, 1.1rem)' : 
                                   'clamp(1.1rem, 3vw, 1.2rem)'
                        }}>
                          {benefit.title}
                        </h4>
                        <p style={{
                          ...styles.cardDescription,
                          fontSize: variant === 'minimal' ? 'clamp(0.75rem, 2vw, 0.85rem)' : 
                                   variant === 'compact' ? 'clamp(0.8rem, 2.2vw, 0.9rem)' : 
                                   'clamp(0.9rem, 2.5vw, 1rem)'
                        }}>
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyChooseVallenAI;
